import React, { useState, useEffect } from "react";
import { Container, Form, Button, Row, Col, Card, ListGroup, Accordion, Offcanvas } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../services/firebase.Config";
import Sidebar from "../../general/sidebar/Sidebar";
import "./AdminPanelStyle.scss";

const ADMIN_EMAILS = new Set([
  "eduardo@metalsoft.com.br",
  "pedro.sousa@metalsoft.com.br",
]);

const isAdminEmail = (email) => ADMIN_EMAILS.has((email || "").toLowerCase());

const AdminPanel = () => {
  const navigate = useNavigate();
  const [obras, setObras] = useState([]);
  const [locations, setLocations] = useState({});
  const [searchTermAdmin, setSearchTermAdmin] = useState("");
  const [currentUrl, setCurrentUrl] = useState(null);
  const [formData, setFormData] = useState({
    obraId: "",
    obraName: "",
    cidade: "",
    estado: "",
    cameras: [{ id: `cam_${Date.now()}`, name: "Câm 01", rtspUrl: "" }],
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [history, setHistory] = useState([]);

  // Extração do nome de usuário a partir do e-mail
  const userEmail = localStorage.getItem("userEmail");
  const isAdmin = isAdminEmail(userEmail);
  const userName = userEmail ? userEmail.split('@')[0] : 'usuário';
  const formattedName = userName.charAt(0).toUpperCase() + userName.slice(1);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && isAdminEmail(user.email)) {
        fetchObras();
        fetchHistory(); 
      } else {
        console.warn("Acesso negado aos dados administrativos.");
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchObras = async () => {
    try {
      const response = await fetch("/api/obras");
      const data = await response.json();
      setObras(data);
      updateStructuredLocations(data);
    } catch (error) {
      console.error("Erro ao buscar obras:", error);
    }
  };

  // Atualizado: Busca na API, mas tem o localStorage como plano de segurança
  const fetchHistory = async () => {
    try {
      const response = await fetch("/api/history");
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setHistory(data);
          localStorage.setItem("adminHistory", JSON.stringify(data));
          return;
        }
      }
    } catch (error) {
      console.error("Erro ao buscar histórico da API:", error);
    }

    // Se a API não retornar dados (ex: backend reiniciou), puxa do navegador
    const cachedHistory = localStorage.getItem("adminHistory");
    if (cachedHistory) {
      setHistory(JSON.parse(cachedHistory));
    }
  };

  const updateStructuredLocations = (data) => {
    const structured = {};
    data.forEach(obra => {
      if (!structured[obra.estado]) structured[obra.estado] = {};
      if (!structured[obra.estado][obra.cidade]) structured[obra.estado][obra.cidade] = [];
      structured[obra.estado][obra.cidade].push(obra);
    });
    setLocations(structured);
  };

  // Atualizado: Salva no localStorage na mesma hora que atualiza a tela
  const addHistoryEntry = async (type, message) => {
    const newEntry = {
      id: Date.now(),
      type, 
      message,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };

    setHistory(prevHistory => {
      const atualizado = [newEntry, ...prevHistory].slice(0, 3);
      localStorage.setItem("adminHistory", JSON.stringify(atualizado));
      return atualizado;
    });

    try {
      await fetch("/api/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newEntry)
      });
    } catch (error) {
      console.error("Erro ao salvar histórico no banco:", error);
    }
  };

  const ordenarCameras = (camerasArray) => {
    return [...camerasArray].sort((a, b) => {
      const extrairNumero = (nome) => {
        const match = (nome || "").match(/\d+/);
        return match ? parseInt(match[0], 10) : 999; 
      };
      return extrairNumero(a.name) - extrairNumero(b.name);
    });
  };

  const filteredLocations = () => {
    if (!searchTermAdmin) return locations;

    const filtered = {};
    const term = searchTermAdmin.toLowerCase();

    Object.entries(locations).forEach(([estado, cidades]) => {
      const filteredCidades = {};
      Object.entries(cidades).forEach(([cidade, obrasList]) => {
        const matches = obrasList.filter(o =>
          o.nome.toLowerCase().includes(term) ||
          cidade.toLowerCase().includes(term) ||
          estado.toLowerCase().includes(term)
        );
        if (matches.length > 0) {
          filteredCidades[cidade] = matches;
        }
      });

      if (Object.keys(filteredCidades).length > 0) {
        filtered[estado] = filteredCidades;
      }
    });

    return filtered;
  };

  const totalObras = obras.length;
  const totalEstados = Object.keys(locations).length;
  
  let totalCidades = 0;
  Object.values(locations).forEach(cidades => {
    totalCidades += Object.keys(cidades).length;
  });

  const totalCameras = obras.reduce((acc, obra) => {
    if (Array.isArray(obra.cameras)) {
      return acc + obra.cameras.length;
    } else if (typeof obra.cameras === 'object' && obra.cameras !== null) {
      return acc + Object.keys(obra.cameras).length;
    } else if (typeof obra.cameraCount === 'number') {
      return acc + obra.cameraCount;
    }
    return acc;
  }, 0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCameraChange = (index, field, value) => {
    const currentCameras = formData.cameras || [];
    const newCameras = [...currentCameras];
    newCameras[index][field] = value;
    setFormData({ ...formData, cameras: newCameras });
  };

  const handleCameraBlur = () => {
    setFormData(prev => ({
      ...prev,
      cameras: ordenarCameras(prev.cameras || [])
    }));
  };

  const addCamera = () => {
    const currentCameras = formData.cameras || []; 
    
    const currentNumbers = currentCameras.map(c => {
      const match = (c.name || "").match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    });
    
    const maxNum = currentNumbers.length > 0 ? Math.max(...currentNumbers) : 0;
    const nextName = `Câm ${String(maxNum + 1).padStart(2, "0")}`;

    const novaCamera = { 
      id: `cam_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name: nextName, 
      rtspUrl: "" 
    };

    const camerasAtualizadas = ordenarCameras([...currentCameras, novaCamera]);

    setFormData({
      ...formData,
      cameras: camerasAtualizadas,
    });
  };

  const removeCamera = (index) => {
    const currentCameras = formData.cameras || [];
    const newCameras = currentCameras.filter((_, i) => i !== index);
    setFormData({ ...formData, cameras: newCameras });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const shortName = formData.obraName.split(" -")[0].split(" --")[0];
    const url = shortName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "-")
      .replace(/[^a-z0-9-]/g, "");

    const payload = {
      ...formData,
      url,
      oldUrl: isEditing ? currentUrl : null,
      obraId: formData.obraId || Date.now(),
      cameras: ordenarCameras(formData.cameras || []),
    };

    try {
      const response = await fetch("/api/obras", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        await addHistoryEntry(isEditing ? 'edit' : 'add', `${isEditing ? 'Editada' : 'Criada'} obra "${formData.obraName}" por ${formattedName}`);
        
        alert("Obra salva com sucesso!");
        fetchObras(); 
        resetForm();  
      } else {
        alert("Erro ao salvar obra: " + (responseData.message || "Erro desconhecido"));
      }
    } catch (error) {
      console.error("Erro ao salvar obra:", error);
      alert("Erro de conexão com o servidor.");
    }
  };

  const resetForm = () => {
    setFormData({
      obraId: "",
      obraName: "",
      cidade: "",
      estado: "",
      cameras: [{ id: `cam_${Date.now()}`, name: "Câm 01", rtspUrl: "" }],
    });
    setCurrentUrl(null);
    setIsEditing(false);
    setShowOffcanvas(false);
  };

  const handleEdit = async (obraUrl) => {
    try {
      const response = await fetch(`/api/admin/obras/${obraUrl}`);
      const data = await response.json();
      
      const camerasCarregadas = Array.isArray(data.cameras) && data.cameras.length > 0 
        ? data.cameras 
        : [{ id: `cam_${Date.now()}`, name: "Câm 01", rtspUrl: "" }];

      setFormData({
        ...data,
        url: obraUrl,
        cameras: ordenarCameras(camerasCarregadas)
      });
      
      setCurrentUrl(obraUrl);
      setIsEditing(true);
      setShowOffcanvas(true);
    } catch (error) {
      console.error("Erro ao carregar obra para edição:", error);
    }
  };

  const handleDeleteObra = async (obraUrl) => {
    const obraParaExcluir = obras.find(o => o.url === obraUrl);
    const nomeObra = obraParaExcluir ? obraParaExcluir.nome : "Obra desconhecida";

    if (window.confirm(`Tem certeza que deseja excluir a obra ${nomeObra}?`)) {
      try {
        const response = await fetch(`/api/obras/${obraUrl}`, { method: "DELETE" });
        if (response.ok) {
          await addHistoryEntry('delete', `Excluída obra "${nomeObra}" por ${formattedName}`);
          fetchObras();
        }
      } catch (error) {
        console.error("Erro ao excluir obra:", error);
      }
    }
  };

  const handleDeleteCity = async (estado, cidade) => {
    if (window.confirm(`Isso excluirá TODAS as obras de ${cidade} - ${estado}. Confirmar?`)) {
      try {
        const response = await fetch("/api/locations/city", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estado, cidade }),
        });
        if (response.ok) {
          await addHistoryEntry('delete', `Excluída cidade "${cidade}" por ${formattedName}`);
          fetchObras();
        }
      } catch (error) {
        console.error("Erro ao excluir cidade:", error);
      }
    }
  };

  const handleDeleteState = async (estado) => {
    if (window.confirm(`Isso excluirá TODAS as obras do estado ${estado}. Confirmar?`)) {
      try {
        const response = await fetch("/api/locations/state", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estado }),
        });
        if (response.ok) {
          await addHistoryEntry('delete', `Excluído estado "${estado}" por ${formattedName}`);
          fetchObras();
        }
      } catch (error) {
        console.error("Erro ao excluir estado:", error);
      }
    }
  };

  const getHistoryIcon = (type) => {
    if (type === 'add') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
    if (type === 'edit') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
    if (type === 'delete') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;
    return null;
  };

  const formContent = () => (
    <Form onSubmit={handleSubmit} className="mt-4">
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold text-uppercase small text-secondary">Nome da Obra</Form.Label>
        <Form.Control
          type="text"
          name="obraName"
          value={formData.obraName}
          onChange={handleInputChange}
          required
          placeholder="Ex: Residencial Tokyo"
          className="custom-input"
        />
      </Form.Group>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-uppercase small text-secondary">Cidade</Form.Label>
            <Form.Control
              type="text"
              name="cidade"
              value={formData.cidade}
              onChange={handleInputChange}
              required
              placeholder="Ex: Manaus"
              className="custom-input"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-uppercase small text-secondary">Estado</Form.Label>
            <Form.Control
              type="text"
              name="estado"
              value={formData.estado}
              onChange={handleInputChange}
              required
              placeholder="Ex: Amazonas"
              className="custom-input"
            />
          </Form.Group>
        </Col>
      </Row>

      <hr className="my-5 border-light" />

      <div className="cameras-section mb-4 p-4 rounded-4 bg-light border border-light-subtle">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              <h6 className="mb-0 fw-bold text-dark">Câmeras da Obra</h6>
          </div>
          <Button type="button" variant="white" size="sm" className="btn-add-camera text-success fw-bold border shadow-sm px-3" onClick={addCamera}>
            + Adicionar Câmera
          </Button>
        </div>

        <div className="camera-list d-flex flex-column gap-3">
          {(formData.cameras || []).map((camera, index) => (
            <div 
              key={camera.id} 
              className="camera-row-item d-flex flex-column flex-sm-row align-items-sm-center gap-3 p-3 border rounded-3 bg-white shadow-sm transition-all"
            >
              <div className="d-flex align-items-center flex-grow-1 gap-3 w-100">
                <Form.Control
                  type="text"
                  value={camera.name}
                  onChange={(e) => handleCameraChange(index, "name", e.target.value)}
                  onBlur={handleCameraBlur}
                  placeholder="Nome (Ex: Câm 01)"
                  className="camera-name-input fw-bold text-secondary flex-shrink-0 bg-light border-0"
                  style={{ width: "140px" }}
                  required
                />
                <Form.Control
                  type="text"
                  placeholder="URL de transmissão RTSP"
                  value={camera.rtspUrl || ""}
                  onChange={(e) => handleCameraChange(index, "rtspUrl", e.target.value)}
                  required
                  className="custom-input camera-input border-0 bg-light flex-grow-1 px-3"
                />
              </div>
              <Button 
                type="button" 
                variant="light" 
                className="text-danger p-2 btn-remove opacity-75 rounded-3 align-self-end align-self-sm-auto mt-2 mt-sm-0 border border-light" 
                onClick={() => removeCamera(index)} 
                disabled={(formData.cameras || []).length <= 1}
                title="Excluir câmera"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </Button>
            </div>
          ))}
        </div>
      </div>

      <Button type="submit" className="w-100 py-3 fw-bold btn-submit border-0 shadow-sm mt-3">
        {isEditing ? "Salvar Alterações" : "Criar Obra"}
      </Button>
      
      {isEditing && (
        <Button type="button" variant="light" onClick={resetForm} className="w-100 mt-3 py-2 text-secondary fw-semibold border-0 hover-bg-light">
          Cancelar Edição
        </Button>
      )}
    </Form>
  );

  return (
    <main id="page-admin">
      <Sidebar />
      <Container fluid className="py-4 admin-content">
        <div className="admin-container-inner">
          
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
            <div>
              <h1 className="display-6 fw-bold admin-title mb-1">Gestão de obras</h1>
              <p className="text-muted admin-subtitle mb-0">Gerencie as obras, localidades e as transmissões de vídeo RTSP.</p>
            </div>
            {isAdmin && (
              <div className="admin-badge mt-3 mt-md-0 shadow-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                Modo Administrador
              </div>
            )}
          </div>

          {!isAdmin && (
            <div className="alert alert-warning text-center shadow-sm py-4 border-warning rounded-4">
              <h4 className="fw-bold">Acesso Restrito</h4>
              <p className="mb-0">Você não tem permissão para realizar alterações nesta página.</p>
            </div>
          )}

          {isAdmin && (
            <>
              <Row className="mb-4 g-4">
                <Col md={3} sm={6}>
                  <Card className="summary-card border-0 h-100">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="summary-icon icon-obras me-3 shadow-sm">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                      </div>
                      <div>
                        <div className="summary-label text-uppercase">Total de Obras</div>
                        <div className="summary-value">{totalObras}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="summary-card border-0 h-100">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="summary-icon icon-estados me-3 shadow-sm">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>
                      </div>
                      <div>
                        <div className="summary-label text-uppercase">Estados</div>
                        <div className="summary-value">{totalEstados}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="summary-card border-0 h-100">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="summary-icon icon-cidades me-3 shadow-sm">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                      </div>
                      <div>
                        <div className="summary-label text-uppercase">Cidades</div>
                        <div className="summary-value">{totalCidades}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="summary-card border-0 h-100">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="summary-icon icon-cameras me-3 shadow-sm">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
                      </div>
                      <div>
                        <div className="summary-label text-uppercase">Câmeras</div>
                        <div className="summary-value">{totalCameras}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {history.length > 0 && (
                <Row className="mb-5">
                  <Col>
                    <Card className="border-0 shadow-sm rounded-4 bg-white" style={{ background: "linear-gradient(to right, #ffffff, #f8fafc)" }}>
                      <Card.Body className="p-3 px-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                          <div className="bg-light p-2 rounded-circle">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                          </div>
                          <span className="fw-bold text-dark" style={{ fontSize: "0.95rem" }}>Atividades Recentes</span>
                        </div>
                        
                        <div className="d-flex flex-wrap gap-2 justify-content-lg-end w-100">
                          {history.map(item => (
                            <div key={item.id} className="d-flex align-items-center border rounded-pill px-3 py-1 bg-white shadow-sm" style={{ fontSize: "0.85rem" }}>
                              <span className="me-2 d-flex align-items-center">
                                {getHistoryIcon(item.type)}
                              </span>
                              <span className="text-secondary fw-medium me-3 text-truncate" style={{ maxWidth: "300px" }}>{item.message}</span>
                              <span className="text-muted small border-start ps-2 border-light-subtle">{item.time}</span>
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <Row className="gx-lg-5">
                <Col lg={5} className="mb-4 mb-lg-0">
                  <Card className="shadow-sm border-0 form-card h-100">
                    <Card.Body className="p-4 p-md-5">
                      {!isEditing ? (
                        <>
                          <div className="d-flex align-items-center mb-2">
                            <div className="plus-icon-wrapper me-3 shadow-sm">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </div>
                            <h4 className="mb-0 fw-bold text-dark">Nova Obra</h4>
                          </div>
                          <p className="text-muted small mb-4 ms-5">Cadastre um novo empreendimento e suas câmeras</p>
                          {formContent()}
                        </>
                      ) : (
                        <div className="text-center py-5 h-100 d-flex flex-column justify-content-center">
                          <div className="mb-4">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                          </div>
                          <h5 className="fw-bold text-dark mb-2">Modo de Edição Ativo</h5>
                          <p className="text-muted small mb-4 px-3">Você está editando a obra <strong className="text-primary">{formData.obraName}</strong> no painel lateral.</p>
                          <div className="d-flex justify-content-center gap-2">
                            <Button variant="outline-primary" className="fw-semibold px-4" onClick={() => setShowOffcanvas(true)}>Reabrir Painel</Button>
                            <Button variant="outline-danger" className="fw-semibold px-4" onClick={resetForm}>Cancelar Edição</Button>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card className="shadow-sm border-0 h-100 list-card">
                    <Card.Body className="p-4 p-md-5">
                      <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-5 gap-3">
                        <div>
                            <h4 className="mb-1 fw-bold text-dark">Localidades</h4>
                            <p className="text-muted small mb-0">Explore e gerencie as obras por região</p>
                        </div>
                        <div className="search-admin" style={{ maxWidth: "320px", width: "100%" }}>
                            <div className="position-relative shadow-sm rounded-pill">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="position-absolute top-50 start-0 translate-middle-y ms-3"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                <Form.Control
                                    type="text"
                                    placeholder="Pesquisar obra, cidade ou estado..."
                                    value={searchTermAdmin}
                                    onChange={(e) => setSearchTermAdmin(e.target.value)}
                                    className="search-input ps-5 py-2 border-0 bg-light"
                                />
                            </div>
                        </div>
                      </div>

                      <div className="list-content">
                        {Object.keys(filteredLocations()).length > 0 ? (
                            <Accordion className="custom-accordion d-flex flex-column gap-3">
                                {Object.entries(filteredLocations()).map(([estado, cidades], idx) => (
                                    <Accordion.Item eventKey={String(idx)} key={estado} className="border-0 rounded-4 shadow-sm overflow-hidden">
                                        <Accordion.Header>
                                            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="fw-bold text-dark fs-6">{estado}</span>
                                                    <span className="badge-count bg-light text-secondary px-3 py-1 rounded-pill small fw-semibold">
                                                        {Object.keys(cidades).length} Cidade(s)
                                                    </span>
                                                </div>
                                                <Button type="button" variant="link" className="text-danger btn-sm p-0 fw-semibold text-decoration-none z-index-2 hover-danger" onClick={(e) => { e.stopPropagation(); handleDeleteState(estado); }}>
                                                    Excluir Estado
                                                </Button>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white p-4 border-top">
                                            <Row className="g-4">
                                                {Object.entries(cidades).map(([cidade, obras]) => (
                                                    <Col md={12} xl={6} key={cidade}>
                                                        <div className="city-block border rounded-4 p-4 h-100 bg-light transition-all">
                                                            <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom border-secondary-subtle">
                                                                <span className="fw-bold text-dark">{cidade}</span>
                                                                <Button type="button" variant="white" className="text-danger btn-xs py-1 px-2 border shadow-sm rounded-2 bg-white fw-medium hover-danger-bg" onClick={() => handleDeleteCity(estado, cidade)}>
                                                                    Remover
                                                                </Button>
                                                            </div>
                                                            <ListGroup variant="flush" className="bg-transparent">
                                                                {obras.map(obra => (
                                                                    <ListGroup.Item key={obra.url} className="d-flex justify-content-between align-items-center px-0 py-2 border-0 bg-transparent">
                                                                        <span className="text-secondary fw-medium text-truncate me-2" style={{ fontSize: "0.9rem", maxWidth: "180px" }} title={obra.nome}>
                                                                            {obra.nome}
                                                                        </span>
                                                                        <div className="d-flex gap-2">
                                                                            <Button type="button" variant="light" className="text-primary px-2 py-1 btn-sm fw-semibold border-0 shadow-sm rounded-2 bg-white" style={{ fontSize: "0.75rem" }} onClick={() => handleEdit(obra.url)}>Editar</Button>
                                                                            <Button type="button" variant="light" className="text-danger px-2 py-1 btn-sm fw-semibold border-0 shadow-sm rounded-2 bg-white" style={{ fontSize: "0.75rem" }} onClick={() => handleDeleteObra(obra.url)}>Excluir</Button>
                                                                        </div>
                                                                    </ListGroup.Item>
                                                                ))}
                                                            </ListGroup>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        ) : (
                            <div className="empty-state text-center py-5 my-4">
                                <div className="empty-icon mb-4 d-inline-flex justify-content-center align-items-center bg-white shadow-sm rounded-circle" style={{ width: '80px', height: '80px' }}>
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                                </div>
                                <h5 className="text-dark fw-bold mb-2">Nenhuma localidade encontrada</h5>
                                <p className="text-muted">As obras cadastradas aparecerão aqui divididas por região.</p>
                            </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </Container>

      <Offcanvas show={showOffcanvas} onHide={resetForm} placement="end" style={{ width: '600px', maxWidth: '100vw' }} className="border-0 shadow-lg">
        <Offcanvas.Header closeButton className="border-bottom px-4 py-4 bg-light">
          <Offcanvas.Title className="fw-bold d-flex align-items-center text-dark">
            <div className="plus-icon-wrapper me-3 shadow-sm bg-white" style={{ width: '32px', height: '32px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </div>
            Editar Empreendimento
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-4 px-md-5">
          <p className="text-muted small mb-2">Altere as informações abaixo e salve para aplicar.</p>
          {formContent()}
        </Offcanvas.Body>
      </Offcanvas>

    </main>
  );
};

export default AdminPanel;